import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Fab,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArchiveIcon from "@mui/icons-material/Archive";
import BlockIcon from "@mui/icons-material/Block";
import DateRangeIcon from "@mui/icons-material/DateRange";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DataTable from "./DataTable";
import VerticalTabs from "./VerticalTabs";
import ArchiveBookingModal from "./ArchiveBookingModal";
import UnavailableDateModal from "./UnavailableDateModal";
import ConfirmationDialog from "./ConfirmationDialog";
import NotificationSnackbar from "./NotificationSnackbar";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { formatLocalDateKey } from "../../../utils/dateUtils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import WeekdaysSettings from "./WeekdaysSettings";
import { ThumbDown, ThumbUp } from "@mui/icons-material";

// Define the tabs for our vertical navigation
const tabs = [
  { id: "bookings", label: "Réservations", icon: <CalendarMonthIcon /> },
  { id: "archived", label: "Archives", icon: <ArchiveIcon /> },
  { id: "unavailable", label: "Jours indisponibles", icon: <BlockIcon /> },
  { id: "weekdays", label: "Jours disponibles", icon: <DateRangeIcon /> },
];

const BookingsTableView = () => {
  // State for active tab and data
  const [activeTab, setActiveTab] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [archivedBookings, setArchivedBookings] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // UI state for modals and notifications
  const [archiveModal, setArchiveModal] = useState({
    open: false,
    booking: null,
  });

  const [unavailableDateModal, setUnavailableDateModal] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    itemToDelete: null,
    itemType: "", // "archive" or "unavailable"
  });

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Format date function for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return format(date, "d MMMM yyyy", { locale: fr });
  };

  // Get status chip for booking
  const getStatusChip = (booking) => {
    return (
      <Chip
        label={booking.isApproved ? "Confirmé" : "En attente"}
        size="small"
        sx={{
          backgroundColor: booking.isApproved ? "#4caf50" : "#9e9e9e",
          color: "white",
          fontWeight: "medium",
        }}
      />
    );
  };

  // Action buttons for bookings
  const getActionButtons = (row, table) => (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {table === "bookings" && (
        <Tooltip title={row.isApproved ? "Désapprouver" : "Approuver"}>
          <IconButton
            size="small"
            color={row?.isApproved ? "error" : "success"}
            onClick={() => handleToggleApproval(row)}
            disabled={isProcessing}
          >
            {row.isApproved ? (
              <ThumbDown fontSize="small" />
            ) : (
              <ThumbUp fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
      {table === "bookings" && (
        <Tooltip title="Archiver">
          <IconButton
            size="small"
            color="default"
            onClick={() => handleArchiveBooking(row)}
            disabled={isProcessing}
          >
            <ArchiveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {table === "archives" && (
        <>
          <Tooltip title="Restaurer">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleRestoreBooking(row)}
              disabled={isProcessing}
            >
              <UnarchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleConfirmDeleteArchive(row)}
              disabled={isProcessing}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );

  // Define columns for active bookings table
  const bookingsColumns = [
    {
      field: "startDate",
      headerName: "Date de début",
      minWidth: 120,
      renderCell: (row) => formatDate(row.startDate),
    },
    {
      field: "endDate",
      headerName: "Date de fin",
      minWidth: 120,
      renderCell: (row) => formatDate(row.endDate),
    },
    {
      field: "name",
      headerName: "Nom",
    },
    {
      field: "email",
      headerName: "Email",
    },
    {
      field: "phone",
      headerName: "Téléphone",
    },
    {
      field: "status",
      headerName: "Statut",
      renderCell: (row) => getStatusChip(row),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 170,
      align: "center",
      renderCell: (row) => getActionButtons(row, "bookings"),
    },
  ];

  const archivesColumns = [
    {
      field: "startDate",
      headerName: "Date de début",
      minWidth: 120,
      renderCell: (row) => formatDate(row.startDate),
    },
    {
      field: "endDate",
      headerName: "Date de fin",
      minWidth: 120,
      renderCell: (row) => formatDate(row.endDate),
    },
    {
      field: "name",
      headerName: "Nom",
    },
    {
      field: "email",
      headerName: "Email",
    },
    {
      field: "phone",
      headerName: "Téléphone",
    },
    {
      field: "cancellationNote",
      headerName: "Note",
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 170,
      align: "center",
      renderCell: (row) => getActionButtons(row, "archives"),
    },
  ];

  // Define columns for unavailable dates table
  const unavailableDatesColumns = [
    {
      field: "date",
      headerName: "Date",
      minWidth: 200,
      renderCell: (row) => formatDate(row.date),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      align: "center",
      renderCell: (row) => (
        <Tooltip title="Supprimer">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleConfirmDeleteUnavailableDate(row)}
            disabled={isProcessing}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Fetch data from Firestore on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Function to fetch all data
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const formatDateField = (data) =>
        data?.toDate ? data.toDate() : new Date(data);

      const formatStartEndDate = (data) => ({
        startDate: data.startDate
          ? formatLocalDateKey(formatDateField(data.startDate))
          : formatLocalDateKey(formatDateField(data.date)),
        endDate: data.endDate
          ? formatLocalDateKey(formatDateField(data.endDate))
          : formatLocalDateKey(formatDateField(data.date)),
      });

      const processCollection = async (collectionName) => {
        const snap = await getDocs(collection(db, collectionName));
        return snap.docs.map((doc) => {
          const data = doc.data();
          const dates = formatStartEndDate(data);
          return {
            id: doc.id,
            ...data,
            ...dates,
          };
        });
      };

      const [bookingsData, archivesData, unavailableSnap] = await Promise.all([
        processCollection("bus_bookings"),
        processCollection("bus_archive"),
        getDocs(collection(db, "bus_unavailable_dates")),
      ]);

      const unavailableDatesData = unavailableSnap.docs.map((doc) => ({
        id: doc.id,
        date: doc.id,
      }));

      setBookings(bookingsData);
      setArchivedBookings(archivesData);
      setUnavailableDates(unavailableDatesData);
    } catch (error) {
      console.error("Error fetching data for tables:", error);
      showNotification("Erreur lors du chargement des données", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Show notification helper
  const showNotification = (message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // Event handlers
  const handleToggleApproval = async (booking) => {
    setIsProcessing(true);
    try {
      const bookingRef = doc(db, "bus_bookings", booking.id);
      await updateDoc(bookingRef, {
        isApproved: !booking.isApproved,
      });

      // Update local state
      setBookings((prev) =>
        prev.map((b) =>
          b.id === booking.id ? { ...b, isApproved: !booking.isApproved } : b
        )
      );

      showNotification(
        `Réservation ${
          !booking.isApproved ? "approuvée" : "désapprouvée"
        } avec succès`,
        "success"
      );
    } catch (error) {
      console.error("Error toggling approval:", error);
      showNotification("Erreur lors de la modification du statut", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle opening archive modal
  const handleArchiveBooking = (booking) => {
    setArchiveModal({
      open: true,
      booking,
    });
  };

  // Handle archive confirmation
  const handleConfirmArchive = async (cancellationNote) => {
    setIsProcessing(true);
    try {
      const booking = archiveModal.booking;

      // Create a new document in the archive collection
      await addDoc(collection(db, "bus_archive"), {
        ...booking,
        cancellationNote,
        archivedAt: new Date(),
      });

      // Delete the booking from the bookings collection
      await deleteDoc(doc(db, "bus_bookings", booking.id));

      // Update local state
      setBookings((prev) => prev.filter((b) => b.id !== booking.id));
      setArchivedBookings((prev) => [
        ...prev,
        {
          ...booking,
          cancellationNote,
          archivedAt: new Date(),
        },
      ]);

      showNotification("Réservation archivée avec succès", "success");
    } catch (error) {
      console.error("Error archiving booking:", error);
      showNotification("Erreur lors de l'archivage de la réservation", "error");
    } finally {
      setArchiveModal({ open: false, booking: null });
      setIsProcessing(false);
    }
  };

  // Handle archive modal close
  const handleCloseArchiveModal = () => {
    setArchiveModal({ open: false, booking: null });
  };

  // Handle confirm delete archive
  const handleConfirmDeleteArchive = (archive) => {
    setConfirmDialog({
      open: true,
      title: "Supprimer l'archive",
      message:
        "Êtes-vous sûr de vouloir supprimer définitivement cette réservation archivée ? Cette action est irréversible.",
      itemToDelete: archive,
      itemType: "archive",
    });
  };

  // Handle delete archive
  const handleDeleteArchive = async () => {
    setIsProcessing(true);
    try {
      const archive = confirmDialog.itemToDelete;

      // Delete the archive
      await deleteDoc(doc(db, "bus_archive", archive.id));

      // Update local state
      setArchivedBookings((prev) => prev.filter((a) => a.id !== archive.id));

      showNotification("Archive supprimée avec succès", "success");
    } catch (error) {
      console.error("Error deleting archive:", error);
      showNotification("Erreur lors de la suppression de l'archive", "error");
    } finally {
      setConfirmDialog({ open: false, itemToDelete: null, itemType: "" });
      setIsProcessing(false);
    }
  };

  // Handle restore booking from archive
  const handleRestoreBooking = async (archive) => {
    setIsProcessing(true);
    try {
      const { cancellationNote, archivedAt, originalId, ...bookingData } =
        archive;

      bookingData.isApproved = false;

      // Restore the booking using originalId
      const targetId = originalId || archive.id;
      const bookingRef = doc(db, "bus_bookings", targetId);
      await setDoc(bookingRef, bookingData);

      // Delete from archive collection
      await deleteDoc(doc(db, "bus_archive", archive.id));

      // Update local state
      setArchivedBookings((prev) => prev.filter((a) => a.id !== archive.id));
      setBookings((prev) => [...prev, { ...bookingData, id: targetId }]);

      showNotification("Réservation restaurée avec succès", "success");
    } catch (error) {
      console.error("Error restoring booking:", error);
      showNotification(
        "Erreur lors de la restauration de la réservation",
        "error"
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle unavailable date modal close
  const handleCloseUnavailableDateModal = () => {
    setUnavailableDateModal(false);
  };

  // Handle unavailable date confirmation
  const handleConfirmUnavailableDate = async (dateKey) => {
    setIsProcessing(true);
    try {
      // Add the unavailable date to Firestore
      await setDoc(doc(db, "bus_unavailable_dates", dateKey), {
        date: dateKey,
      });

      // Update local state
      setUnavailableDates((prev) => [...prev, { id: dateKey, date: dateKey }]);

      showNotification("Date indisponible ajoutée avec succès", "success");
    } catch (error) {
      console.error("Error adding unavailable date:", error);
      showNotification(
        "Erreur lors de l'ajout de la date indisponible",
        "error"
      );
    } finally {
      setUnavailableDateModal(false);
      setIsProcessing(false);
    }
  };

  // Handle confirm delete unavailable date
  const handleConfirmDeleteUnavailableDate = (dateObj) => {
    setConfirmDialog({
      open: true,
      title: "Supprimer la date indisponible",
      message: `Êtes-vous sûr de vouloir supprimer cette date indisponible (${formatDate(
        dateObj.date
      )}) ? Cette action rendra la date disponible pour les réservations.`,
      itemToDelete: dateObj,
      itemType: "unavailable",
    });
  };

  // Handle delete unavailable date
  const handleDeleteUnavailableDate = async () => {
    setIsProcessing(true);
    try {
      const dateObj = confirmDialog.itemToDelete;

      // Delete the unavailable date
      await deleteDoc(doc(db, "bus_unavailable_dates", dateObj.id));

      // Update local state
      setUnavailableDates((prev) => prev.filter((d) => d.id !== dateObj.id));

      showNotification("Date supprimée avec succès", "success");
    } catch (error) {
      console.error("Error deleting unavailable date:", error);
      showNotification("Erreur lors de la suppression de la date", "error");
    } finally {
      setConfirmDialog({ open: false, itemToDelete: null, itemType: "" });
      setIsProcessing(false);
    }
  };

  // Handle confirmation dialog close
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      itemToDelete: null,
      itemType: "",
    });
  };

  // Handle confirmation dialog confirm
  const handleConfirmAction = () => {
    if (confirmDialog.itemType === "archive") {
      handleDeleteArchive();
    } else if (confirmDialog.itemType === "unavailable") {
      handleDeleteUnavailableDate();
    }
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  // Render loading spinner
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#c84653" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {/* Vertical tabs on the left */}
        <Grid item xs={12} md={3}>
          <VerticalTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={handleTabChange}
          />
        </Grid>

        {/* Content area on the right */}
        <Grid item xs={12} md={9}>
          {/* Active Bookings View */}
          {activeTab === 0 && (
            <DataTable
              title="Réservations Actives"
              data={bookings}
              columns={bookingsColumns}
              initialRowsPerPage={10}
              searchEnabled={true}
              searchPlaceholder="Rechercher une réservation..."
              emptyMessage="Aucune réservation active à afficher"
              paperProps={{
                sx: {
                  height: "100%",
                  minHeight: "600px",
                  position: "relative",
                },
              }}
            />
          )}

          {/* Archived Bookings View */}
          {activeTab === 1 && (
            <DataTable
              title="Réservations Archivées"
              data={archivedBookings}
              columns={archivesColumns}
              initialRowsPerPage={10}
              searchEnabled={true}
              searchPlaceholder="Rechercher une réservation archivée..."
              emptyMessage="Aucune réservation archivée à afficher"
              paperProps={{
                sx: {
                  height: "100%",
                  minHeight: "600px",
                  position: "relative",
                },
              }}
            />
          )}

          {/* Unavailable Dates View */}
          {activeTab === 2 && (
            <Box sx={{ position: "relative" }}>
              <DataTable
                title="Jours Indisponibles"
                data={unavailableDates}
                columns={unavailableDatesColumns}
                initialRowsPerPage={10}
                searchEnabled={true}
                searchPlaceholder="Rechercher une date..."
                emptyMessage="Aucune date indisponible à afficher"
                paperProps={{
                  sx: {
                    height: "100%",
                    minHeight: "600px",
                  },
                }}
              />
              <Fab
                color="primary"
                aria-label="add"
                onClick={() => setUnavailableDateModal(true)}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 20,
                  backgroundColor: "#c84653",
                  "&:hover": {
                    backgroundColor: "#a93a46",
                  },
                }}
                disabled={isProcessing}
              >
                <AddIcon />
              </Fab>
            </Box>
          )}

          {/* Available Weekdays View */}
          {activeTab === 3 && (
            <Paper
              elevation={0}
              sx={{
                padding: 3,
                borderRadius: "12px",
                border: "1px solid #f0f0f0",
                height: "100%",
                minHeight: "600px",
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: "bold", color: "#444349" }}
              >
                Jours de la Semaine Disponibles
              </Typography>
              <WeekdaysSettings />
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Modal for archiving a booking */}
      <ArchiveBookingModal
        open={archiveModal.open}
        booking={archiveModal.booking}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
      />

      {/* Modal for adding an unavailable date */}
      <UnavailableDateModal
        open={unavailableDateModal}
        onClose={handleCloseUnavailableDateModal}
        onConfirm={handleConfirmUnavailableDate}
      />

      {/* Confirmation dialog for deletions */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmAction}
      />

      {/* Notification snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />

      {/* Processing overlay */}
      {isProcessing && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#c84653" }} />
        </Box>
      )}
    </Box>
  );
};

export default BookingsTableView;
